import type { StepProgress } from '../api/types';
import { api } from '../api/client';
import './StepProgressBar.css';

interface Props {
  sessionName: string;
  data: StepProgress;
  /** Optional: poll callback to refresh the agent state after sending. */
  onSent?: () => void;
}

/**
 * Renders Claude Code 2.1.x's wizard step indicator that sits above
 * multi-step prompts (e.g. ArTrack's release wizard). The CLI shows it
 * in the pane as one ASCII row:
 *
 *     ←  ☒ Branch-Sync  ☐ Scope  ✔ Submit  →
 *
 * The arrows mean the user can navigate between steps with the keyboard;
 * when `navigable` is true we render ←/→ buttons that POST the matching
 * arrow keypress to the pane. Without arrows the Stepper is read-only.
 */
export function StepProgressBar({ sessionName, data, onSent }: Props) {
  const send = async (key: 'Left' | 'Right') => {
    try {
      await api.sessions.sendKey(sessionName, key);
      if (onSent) onSent();
    } catch (err) {
      console.error('step-progress nav failed', err);
    }
  };

  return (
    <div className="sp-row">
      {data.navigable && (
        <button
          type="button"
          className="sp-nav"
          onClick={() => send('Left')}
          aria-label="Previous step"
          title="Vorheriger Schritt (Pfeil ←)"
        >
          ←
        </button>
      )}
      <ol className="sp-steps">
        {data.steps.map((step, i) => (
          <li key={i} className={`sp-step sp-step-${step.state}`}>
            <span className="sp-marker" aria-hidden="true">
              {step.state === 'done' && '✓'}
              {step.state === 'failed' && '✘'}
              {step.state === 'active' && '●'}
              {step.state === 'pending' && '○'}
              {step.state === 'unknown' && '?'}
            </span>
            <span className="sp-label">{step.label}</span>
          </li>
        ))}
      </ol>
      {data.navigable && (
        <button
          type="button"
          className="sp-nav"
          onClick={() => send('Right')}
          aria-label="Next step"
          title="Nächster Schritt (Pfeil →)"
        >
          →
        </button>
      )}
    </div>
  );
}
